import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import OrderBookTable from './components/OrderBookTable';
import LiquidityHeatmap from './components/LiquidityHeatmap';
import MarketControls from './components/MarketControls';
import TimeAndSales from './components/TimeAndSales';
import SpreadAnalysis from './components/SpreadAnalysis';
import Icon from '../../components/AppIcon';

const RealTimeOrderBook = () => {
  const [selectedContract, setSelectedContract] = useState('EUA-DEC24');
  const [priceGrouping, setPriceGrouping] = useState('0.05');
  const [showAggregated, setShowAggregated] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('orderbook'); // For mobile navigation

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Simulate connection status changes
    const interval = setInterval(() => {
      const statuses = ['connected', 'connecting', 'disconnected'];
      const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
      setConnectionStatus(randomStatus);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    // Trigger refresh across all components
    console.log('Refreshing market data...');
  };

  const mobileTabOptions = [
    { key: 'orderbook', label: 'Order Book', icon: 'BookOpen' },
    { key: 'heatmap', label: 'Heatmap', icon: 'Activity' },
    { key: 'trades', label: 'Trades', icon: 'TrendingUp' },
    { key: 'spread', label: 'Spread', icon: 'BarChart3' },
    { key: 'controls', label: 'Controls', icon: 'Settings' }
  ];

  const renderMobileContent = () => {
    switch (activeTab) {
      case 'orderbook':
        return (
          <OrderBookTable
            selectedContract={selectedContract}
            priceGrouping={priceGrouping}
            showAggregated={showAggregated}
          />
        );
      case 'heatmap':
        return <LiquidityHeatmap selectedContract={selectedContract} />;
      case 'trades':
        return <TimeAndSales selectedContract={selectedContract} />;
      case 'spread':
        return <SpreadAnalysis selectedContract={selectedContract} />;
      case 'controls':
        return (
          <MarketControls
            selectedContract={selectedContract}
            onContractChange={setSelectedContract}
            priceGrouping={priceGrouping}
            onPriceGroupingChange={setPriceGrouping}
            showAggregated={showAggregated}
            onAggregatedChange={setShowAggregated}
            connectionStatus={connectionStatus}
            onRefresh={handleRefresh}
          />
        );
      default:
        return null;
    }
  };

  if (isMobile) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16">
          {/* Mobile Header */}
          <div className="bg-card border-b border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-foreground">Real-Time Order Book</h1>
                <p className="text-sm text-muted-foreground">Live market depth & liquidity analysis</p>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={connectionStatus === 'connected' ? 'Wifi' : 'WifiOff'} 
                  size={16} 
                  className={connectionStatus === 'connected' ? 'text-success' : 'text-error'}
                />
                <span className={`text-xs font-medium ${
                  connectionStatus === 'connected' ? 'text-success' : 'text-error'
                }`}>
                  {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="bg-card border-b border-border">
            <div className="flex overflow-x-auto">
              {mobileTabOptions?.map(tab => (
                <button
                  key={tab?.key}
                  onClick={() => setActiveTab(tab?.key)}
                  className={`
                    flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap
                    border-b-2 transition-colors duration-200
                    ${activeTab === tab?.key
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Content */}
          <div className="p-4">
            {renderMobileContent()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        {/* Desktop Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Real-Time Order Book</h1>
                <p className="text-muted-foreground mt-1">
                  Live market depth visualization and liquidity analysis for EUA & Oil Futures trading
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
                  <Icon 
                    name={connectionStatus === 'connected' ? 'Wifi' : connectionStatus === 'connecting' ? 'Loader' : 'WifiOff'} 
                    size={16} 
                    className={`${
                      connectionStatus === 'connected' ? 'text-success' : 
                      connectionStatus === 'connecting' ? 'text-warning animate-spin' : 'text-error'
                    }`}
                  />
                  <span className={`text-sm font-medium ${
                    connectionStatus === 'connected' ? 'text-success' : 
                    connectionStatus === 'connecting' ? 'text-warning' : 'text-error'
                  }`}>
                    {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
                  </span>
                </div>
                
                <div className="text-xs text-muted-foreground">
                  Last updated: {new Date()?.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Content */}
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-12 gap-6">
            {/* Left Column - Controls */}
            <div className="col-span-3">
              <MarketControls
                selectedContract={selectedContract}
                onContractChange={setSelectedContract}
                priceGrouping={priceGrouping}
                onPriceGroupingChange={setPriceGrouping}
                showAggregated={showAggregated}
                onAggregatedChange={setShowAggregated}
                connectionStatus={connectionStatus}
                onRefresh={handleRefresh}
              />
            </div>

            {/* Center Column - Order Book */}
            <div className="col-span-5">
              <OrderBookTable
                selectedContract={selectedContract}
                priceGrouping={priceGrouping}
                showAggregated={showAggregated}
              />
            </div>

            {/* Right Column - Heatmap */}
            <div className="col-span-4">
              <LiquidityHeatmap selectedContract={selectedContract} />
            </div>

            {/* Bottom Row - Time & Sales and Spread Analysis */}
            <div className="col-span-6">
              <TimeAndSales selectedContract={selectedContract} />
            </div>

            <div className="col-span-6">
              <SpreadAnalysis selectedContract={selectedContract} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeOrderBook;